import React, { useState, useRef, useEffect, useMemo } from "react";
import { FieldArray, Formik } from "formik";
import * as yup from "yup";
import InputFieldFloat from "../../../../../components/inputs/InputFieldFloat";
import InputRichTextAreaFloat from "../../../../../components/inputs/InputRichTextAreaFloat";
import ImageInput from "../../../../../components/inputs/ImageInput";
import Icon from "../../../../icons/Icon";
import { TimesStrokeIcon } from "../../../../icons/Icons";
import { useTranslation } from "react-i18next";

export default function Details({
  submit,
  setSubmit,
  setIsValidated,
  inputInititalValues,
  formData,
  setFormData,
}) {
  const [file, setFile] = useState(formData?.image);
  const [imageUrl, setimageUrl] = useState();
  const [isFileUploaded, setIsFileUploaded] = useState(true);
  const lessonsGeneratedID = useRef(0);
  const skillsGeneratedID = useRef(0);
  const formRef = useRef();

  const { t } = useTranslation("learningPaths");

  const validationSchema = yup.object({
    trackNameAr: yup.string().required(t("learningPaths:pathARNameRequired")),
    trackNameEn: yup.string().required(t("learningPaths:pathENNameRequired")),
    trackDetailsAr: yup.string().required(t("learningPaths:pathARDetailsRequired")),
    trackDetailsEn: yup.string().required(t("learningPaths:pathENDetailsRequired")),
    lessons: yup.array().of(
      yup.object().shape({
        nameAr: yup.string().required(t("learningPaths:pathARPointRequired")),
        nameEn: yup.string().required(t("learningPaths:pathENPointRequired")),
      })
    ),
    skills: yup.array().of(
      yup.object().shape({
        nameAr: yup.string().required(t("learningPaths:pathARSkillRequired")),
        nameEn: yup.string().required(t("learningPaths:pathENSkillRequired")),
      })
    ),
    examTime: yup
      .number()
      .nullable()
      .transform((v) => (v === "" || v === undefined || isNaN(v) ? null : v))
      .positive(t("learningPaths:pathExamTimePositive"))
      .integer(t("learningPaths:pathExamTimeInteger")),
  });
  const isEditMode = Boolean(inputInititalValues?.id);

  const validateArDescription = (arDescription) => {
    if (arDescription === " " || arDescription === undefined || arDescription === "<p><br></p>") {
      return t("learningPaths:pathARDetailsRequired");
    }
  };
  const validateEnDescription = (enDescription) => {
    if (enDescription === " " || enDescription === undefined || enDescription === "<p><br></p>") {
      return t("learningPaths:pathENDetailsRequired");
    }
  };

  const buildInitialValues = (source) => {
    const empty = {
      trackNameAr: "",
      trackNameEn: "",
      trackDetailsAr: "",
      trackDetailsEn: "",
      examTime: "",
      lessons: [{ id: 0, nameAr: "", nameEn: "" }],
      skills: [{ id: 0, nameAr: "", nameEn: "" }],
    };
    if (!source || typeof source !== "object") return empty;
    const titleAr = source.title_ar ?? "";
    const titleEn = source.title_en ?? "";
    const descAr = source.description_ar ?? "";
    const descEn = source.description_en ?? "";
    const keyPointsAr = source.key_points_ar;
    const keyPointsEn = source.key_points_en;
    const skillsAr = source.skills_ar;
    const skillsEn = source.skills_en;
    const lessons = Array.isArray(keyPointsAr)
      ? keyPointsAr.map((keyPoint, index) => ({
          id: index,
          nameAr: keyPoint ?? "",
          nameEn: keyPointsEn?.[index] ?? keyPoint ?? "",
        }))
      : [{ id: 0, nameAr: "", nameEn: "" }];
    const skills = Array.isArray(skillsAr)
      ? skillsAr.map((skillAr, index) => ({
          id: index,
          nameAr: skillAr ?? "",
          nameEn: skillsEn?.[index] ?? skillAr ?? "",
        }))
      : [{ id: 0, nameAr: "", nameEn: "" }];
    return {
      trackNameAr: titleAr,
      trackNameEn: titleEn,
      trackDetailsAr: descAr,
      trackDetailsEn: descEn,
      examTime:
        source.exam_time != null ? String(source.exam_time) : "",
      lessons,
      skills,
    };
  };

  const initialValues = useMemo(
    () => buildInitialValues(inputInititalValues),
    [inputInititalValues]
  );

  useEffect(() => {
    if (!inputInititalValues) return;
    setimageUrl(inputInititalValues.image_url);
    const lessons = inputInititalValues.key_points_ar ?? [];
    const skills = inputInititalValues.skills_ar ?? [];
    lessonsGeneratedID.current = lessons.length;
    skillsGeneratedID.current = skills.length;
  }, [inputInititalValues]);

  useEffect(() => {
    if (!formData) return;
    if (formData.image) {
      setimageUrl(URL.createObjectURL(formData.image));
    }
    const lessons = formData.key_points_ar ?? [];
    const skills = formData.skills_ar ?? [];
    lessonsGeneratedID.current = lessons.length;
    skillsGeneratedID.current = skills.length;
  }, [formData]);

  useEffect(() => {
    if (submit && formRef.current) {
      formRef.current.handleSubmit();
      setSubmit(null);
    }
  }, [submit, setSubmit]);

  useEffect(() => {
    if (!formData) {
      return;
    }
    if (!file) {
      setFormData({ ...formData, image: null });
      setimageUrl(null);
    }
    // eslint-disable-next-line
  }, [file, setFormData]);

  function populateFormData(values, newFile = null) {
    let dataForm = {};
    const keyPointsEn = values?.lessons?.map((lesson) => lesson.nameEn.trim());
    const keyPointsAr = values?.lessons?.map((lesson) => lesson.nameAr.trim());
    const skillsEn = values?.skills?.map((skill) => skill.nameEn.trim());
    const skillsAr = values?.skills?.map((skill) => skill.nameAr.trim());
    dataForm.title_en = values?.trackNameEn.trim();
    dataForm.title_ar = values?.trackNameAr.trim();
    dataForm.description_en = values?.trackDetailsEn?.trim();
    dataForm.description_ar = values?.trackDetailsAr?.trim();
    dataForm.key_points_en = keyPointsEn;
    dataForm.key_points_ar = keyPointsAr;
    dataForm.skills_en = skillsEn;
    dataForm.skills_ar = skillsAr;
    const examTimeStr =
      values.examTime != null ? String(values.examTime).trim() : "";
    dataForm.exam_time =
      examTimeStr !== "" ? Number(values.examTime) : null;
    if (newFile) {
      dataForm.image = newFile;
      setimageUrl(URL.createObjectURL(newFile));
    } else if (file) {
      dataForm.image = file;
      setimageUrl(URL.createObjectURL(file));
    }
    setFormData(dataForm);
  }

  function addImage(e, values) {
    const imageFile = e?.target?.files[0];
    setFile(imageFile);
    setIsFileUploaded(true);
    populateFormData(values, imageFile);
    setimageUrl(URL.createObjectURL(imageFile));
  }
  function removeImage(values) {
    setFile(null);
    setIsFileUploaded(false);
    populateFormData(values);
    setimageUrl(null);
  }

  function isImagePresent(e) {
    if (!imageUrl) {
      setIsFileUploaded(false);
      return false;
    }
    return true;
  }

  function removedSpacesEvent(e) {
    e.target.value = e.target.value.replace(/\s+/g, " ");
    return e;
  }

  return (
    <div
      className="mt-2 container-fluid tab-pane fade show active edit-learning-path "
      id="nav-home"
      role="tabpanel"
      aria-labelledby="nav-home-tab"
    >
      <Formik
        key={`edit-form-${inputInititalValues?.id ?? "new"}`}
        innerRef={formRef}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        validate={(values) => {
          const errors = {};
          setIsValidated(false);
          if (!isEditMode && !isImagePresent()) {
            errors.file = "file is required";
          }
          const checkArDescription = validateArDescription(values.trackDetailsAr);
          if (checkArDescription) {
            errors.trackDetailsAr = checkArDescription;
          }
          const checkEnDescription = validateEnDescription(values.trackDetailsEn);
          if (checkEnDescription) {
            errors.trackDetailsEn = checkEnDescription;
          }
          populateFormData(values);
          return errors;
        }}
        onSubmit={() => {
          try {
            setIsValidated(true);
          } catch (e) {}
        }}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
          <form className="p-4" onSubmit={handleSubmit}>
            <div>
              <h1 className="mb-4 font_20 font-extrabold"> {t("learningPaths:pathInfo")} </h1>
              <div className="row">
                <div className="col-12 col-md-6">
                  <InputFieldFloat
                    label={t("learningPaths:trackNameAr")}
                    type="text"
                    name="trackNameAr"
                    value={values.trackNameAr}
                    onChange={(e) => handleChange(removedSpacesEvent(e))}
                    error={errors.trackNameAr}
                    touched={touched.trackNameAr}
                  />

                  <InputRichTextAreaFloat
                    label={t("learningPaths:trackDetailsAr")}
                    type="text"
                    id="trackDetailsAr"
                    name="trackDetailsAr"
                    rows="10"
                    value={values.trackDetailsAr}
                    setFieldValue={setFieldValue}
                    error={errors.trackDetailsAr}
                    touched={touched.trackDetailsAr}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <InputFieldFloat
                    label={t("learningPaths:trackNameEn")}
                    type="text"
                    name="trackNameEn"
                    value={values.trackNameEn}
                    onChange={(e) => handleChange(removedSpacesEvent(e))}
                    error={errors.trackNameEn}
                    touched={touched.trackNameEn}
                  />

                  <InputRichTextAreaFloat
                    label={t("learningPaths:trackDetailsEn")}
                    type="text"
                    id="trackDetailsEn"
                    name="trackDetailsEn"
                    rows="10"
                    value={values.trackDetailsEn}
                    setFieldValue={setFieldValue}
                    error={errors.trackDetailsEn}
                    touched={touched.trackDetailsEn}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h1 className="font_20 font-extrabold">{t("learningPaths:trackCoverPhoto")}</h1>
              <p className="font-16 font-color-grey"> {t("learningPaths:trackCoverPhotoHint")}</p>

              <div className="row">
                <div className="col-12 col-md-6">
                  <ImageInput
                    className="learning-path-details-card-image"
                    id="learning-track-avatar"
                    name="learningTrackAvatar"
                    accept=".png, .jpg, .jpeg"
                    imageUrl={imageUrl}
                    onChange={(e) => addImage(e, values)}
                    onDelete={() => removeImage(values)}
                    onBlur={isImagePresent}
                    error={t("learningPaths:trackCoverPhotoError")}
                    touched={!isFileUploaded}
                    file={file}
                    uploadLabel={t("learningPaths:trackCoverUploadLabel")}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h1 className="font_20 font-extrabold">
                {t("learningPaths:pathExamTitle")}
              </h1>
              <p className="font-16 font-color-grey mb-3">
                {t("learningPaths:pathExamTimeHint")}
              </p>
              <div className="row">
                <div className="col-12 col-md-6">
                  <InputFieldFloat
                    label={t("learningPaths:pathExamTimeLabel")}
                    type="number"
                    name="examTime"
                    value={values.examTime}
                    onChange={handleChange}
                    error={errors.examTime}
                    touched={touched.examTime}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h1 className="font_20 font-extrabold">
                {t("learningPaths:trackLearningObjectives")}
              </h1>
              <p className="font-16 font-color-grey">
                {" "}
                {t("learningPaths:trackLearningObjectivesHint")}{" "}
              </p>

              <FieldArray name="lessons">
                {({ push, remove }) => (
                  <div>
                    {values.lessons.map((lesson, index) => {
                      const lessonErrors = (errors.lessons?.length && errors.lessons[index]) || {};
                      const lessonTouched =
                        (touched.lessons?.length && touched.lessons[index]) || {};
                      return (
                        <div
                          className="d-flex align-items-center align-items-md-start gap-2 mb-3 mb-md-4"
                          key={`lesson-${lesson.id}`}
                        >
                          <div className="flex-grow-1 d-md-flex gap-3">
                            <div className="flex-fill mb-3 mb-md-0">
                              <InputFieldFloat
                                label={t("learningPaths:trackLessonsAr", {
                                  index: index + 1,
                                })}
                                type="text"
                                name={`lessons.${index}.nameAr`}
                                value={lesson.nameAr}
                                onChange={(e) => handleChange(removedSpacesEvent(e))}
                                error={lessonErrors.nameAr}
                                touched={lessonTouched.nameAr}
                              />
                            </div>
                            <div className="flex-fill mb-3 mb-md-0">
                              <InputFieldFloat
                                label={t("learningPaths:trackLessonsEn", {
                                  index: index + 1,
                                })}
                                type="text"
                                name={`lessons.${index}.nameEn`}
                                value={lesson.nameEn}
                                onChange={(e) => handleChange(removedSpacesEvent(e))}
                                error={lessonErrors.nameEn}
                                touched={lessonTouched.nameEn}
                              />
                            </div>
                          </div>

                          {values.lessons.length > 1 && (
                            <button
                              type="button"
                              className="border border-2 border-radius-12 border-color flex align-items-center justify-content-between  remove-btn"
                              id="deleteDepartmentButton"
                              onClick={() => remove(index)}
                            >
                              <Icon
                                icon={<TimesStrokeIcon />}
                                iconName="times"
                                iconSize={20}
                                height={20}
                                viewBox="0 0 20 20"
                                iconColor="#bbbabf"
                                hasStroke
                              />
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <div className="row">
                      <button
                        className="col gray-btn"
                        type="button"
                        onClick={() => {
                          push({ id: Math.random(), nameAr: "", nameEn: "" });
                        }}
                      >
                        <img className="ms-3" src="/images/icons/plusW.svg" alt="add point" />
                        {t("learningPaths:addTrackLesson")}
                      </button>
                    </div>
                  </div>
                )}
              </FieldArray>
            </div>

            <div className="mt-4">
              <h1 className="font_20 font-extrabold">{t("learningPaths:learnedSkills")} </h1>
              <p className="font-16 font-color-grey"> {t("learningPaths:learnedSkillHint")}</p>

              <FieldArray name="skills">
                {({ push, remove }) => (
                  <div>
                    {values.skills.map((skill, index) => {
                      const skillErrors = (errors.skills?.length && errors.skills[index]) || {};
                      const skillTouched = (touched.skills?.length && touched.skills[index]) || {};
                      return (
                        <div
                          key={`skill-${index}`}
                          className="d-flex align-items-center align-items-md-start gap-2 mb-3 mb-md-4"
                        >
                          <div className="flex-grow-1 d-md-flex gap-3">
                            <div className="flex-fill mb-3 mb-md-0">
                              <InputFieldFloat
                                label={t("learningPaths:trackSkillsAr", {
                                  index: index + 1,
                                })}
                                type="text"
                                name={`skills.${index}.nameAr`}
                                value={skill.nameAr}
                                onChange={(e) => handleChange(removedSpacesEvent(e))}
                                error={skillErrors.nameAr}
                                touched={skillTouched.nameAr}
                              />
                            </div>
                            <div className="flex-fill mb-3 mb-md-0">
                              <InputFieldFloat
                                label={t("learningPaths:trackSkillsEn", {
                                  index: index + 1,
                                })}
                                type="text"
                                name={`skills.${index}.nameEn`}
                                value={skill.nameEn}
                                onChange={(e) => handleChange(removedSpacesEvent(e))}
                                error={skillErrors.nameEn}
                                touched={skillTouched.nameEn}
                              />
                            </div>
                          </div>

                          {values.skills.length > 1 && (
                            <button
                              type="button"
                              className="border border-2 border-radius-12 border-color flex align-items-center justify-content-between remove-btn"
                              id="deleteDepartmentButton"
                              onClick={() => remove(index)}
                            >
                              <Icon
                                icon={<TimesStrokeIcon />}
                                iconName="times"
                                iconSize={20}
                                height={20}
                                viewBox="0 0 20 20"
                                iconColor="#bbbabf"
                                hasStroke
                              />
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <div className="row">
                      <button
                        className="col gray-btn"
                        type="button"
                        onClick={() => {
                          push({ id: Math.random(), nameAr: "", nameEn: "" });
                        }}
                      >
                        <img className="ms-3" src="/images/icons/plusW.svg" alt="add skill" />
                        {t("learningPaths:addTrackSkill")}
                      </button>
                    </div>
                  </div>
                )}
              </FieldArray>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}
